import React, { useEffect, useRef } from "react";
import Navbar from "../landing/Navbar";
import { Button } from "../components/ui/button";
import { ArrowRight, ExternalLink, Sparkles, Users, Box, ChevronRight, Play, Globe, Activity, ShieldCheck, BarChart3, Radio } from "lucide-react";
import GlowingText from "landing/GlowingText";
import HeroSection from "landing/HeroSection";
import Background from "landing/AnimatedBackground";
import FeaturesSection from "landing/FeaturesSection";

const STATS = [
  { value: '4,000+', label: 'Customers' },
  { value: '500+', label: 'Production Companies' },
  { value: '99.9%', label: 'Uptime' },
  { value: '60 Seconds', label: 'To Book A Shoot' },
];

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-dark-grey text-white font-sans selection:bg-golden selection:text-[#1D1D1B] overflow-x-hidden scroll-smooth">
      <Navbar />

      {/* --- HERO SECTION ---  */}
      <HeroSection />

      {/* --- STATS SECTION --- */}
      <section id="stats-section" className="bg-black py-5 top-shadow z-50">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 relative">
          {STATS.map((stat, i) => (
            <div
              key={i}
              className="relative flex flex-col items-center justify-center text-center"
            >
              {/* Separator - vertical (Desktop/Large Screen) */}
              {/* Renders a separator between all items except the last one */}
              {i !== STATS.length - 1 && (
                <img
                  src="/images/misc/Separator.svg"
                  alt=""
                  className="hidden lg:block absolute -right-[28px] top-1/2 -translate-y-1/2 h-full opacity-80"
                />
              )}

              {/* Separator - mobile (2-column layout) */}
              {/* Renders a separator only after the first column item (i=0, 2, 4...)
             and skips the last item */}
              {i % 2 === 0 && i !== STATS.length - 1 && (
                <img
                  src="/images/misc/Separator.svg"
                  alt=""
                  className="lg:hidden absolute -right-[16px] top-1/2 -translate-y-1/2 h-[50px] w-auto opacity-40"
                />
              )}

              <h3 className="text-3xl md:text-[44px] font-bold text-[#E8D1AB] mb-6 whitespace-nowrap">
                {stat.value}
              </h3>

              <p className="text-sm md:text-xl text-white/70 font-light leading-snug">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* --- Video SECTION --- */}
      <section
        id="video-section"
        className="pt-12 pb-8 md:py-32 relative overflow-hidden"
      >
        <BackgroundVideo />

        <div className="absolute inset-0 bg-black/70 z-0" />

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-golden/5 blur-[120px] rounded-full pointer-events-none z-0" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mt-12 mb-6 md:my-40">
            <h2 className="text-5xl md:text-7xl font-semibold mb-6 text-white tracking-tight">
              Everything You <br />
              Need to <span className="text-golden">Scale</span>
            </h2>
            <p className="text-white max-w-2xl lg:max-w-5xl mx-auto text-xl md:text-2xl font-light leading-relaxed">
              The all-in-one platform for production houses and livestream
              companies to manage crew, equipment, and multi-platform streaming
              — powered by AI.
            </p>
          </div>
        </div>
      </section>
      {/* --- FEATURES SECTION --- */}
      <div id="features" className="scroll-mt-20 md:scroll-mt-24">
        <FeaturesSection />
      </div>
      {/* --- HOW IT WORKS SECTION --- */}
      <section
        id="how-it-works"
        className="scroll-mt-20 md:scroll-mt-24 py-14 md:pt-28 md:pb-0 px-4 md:px-0 bg-dark-grey relative"
      >
        <div className="container mx-auto">
          <div className="text-center mb-14 md:mb-20 md:px-[120px]">
            <h2 className="text-5xl md:text-7xl font-semibold mb-4 md:mb-6 text-white">
              How <span className="text-golden">Beige</span> Works
            </h2>
            <p className="text-white max-w-4xl mx-auto text-2xl font-light leading-relaxed">
              The all-in-one platform for production houses and livestream
              companies to manage crew, equipment, and multi-platform streaming
              — powered by AI.
            </p>
          </div>
        </div>

        {/* max-w-6xl mx-auto  */}
        {/* Timeline / Steps */}
        <div className="w-full border-t border-beige md:border-b md:px-[120px]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 relative md:border-l border-beige">
            {/* Connector Line (Desktop) */}
            {/* <div className="hidden md:block absolute top-12 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent z-0" /> */}

            <StepCard
              number="01"
              title="Define Requirements"
              description="Tell us about your livestream event — date, location, technical needs, and crew requirements."
            />
            <StepCard
              number="02"
              title={"AI Finds\nYour Team"}
              description="Our AI matches you with the perfect crew members and equipment from our marketplace pool."
            />
            <StepCard
              number="03"
              title={"Manage &\nExecute"}
              description="Coordinate your team, track tasks, and manage equipment all in one centralized platform."
            />
            <StepCard
              number="04"
              title={"Go\nLive"}
              description="Stream to multiple platforms simultaneously with our integrated live streaming controls."
            />
          </div>
        </div>
      </section>

      {/* --- CTA SECTION --- */}
      <section
        id="cta-section"
        className="relative py-32 px-4 md:px-0 overflow-hidden bg-dark-grey-2"
      >
        {/* Top-left corner glow */}
        <div
          className="pointer-events-none absolute -top-48 -left-48 w-[700px] h-[700px] blur-3xl rounded-full opacity-100"
          style={{
            background:
              "radial-gradient(circle, rgba(225, 202, 161, 0.20) 0%, transparent 65%)",
          }}
        />

        {/* Bottom-right corner glow */}
        <div
          className="pointer-events-none absolute -bottom-48 -right-48 w-[700px] h-[700px] blur-3xl rounded-full opacity-100"
          style={{
            background:
              "radial-gradient(circle, rgba(225, 202, 161, 0.20) 0%, transparent 65%)",
          }}
        />

        {/* Animated background */}
        <Background />

        <div className="container mx-auto relative z-10">
          <div
            className="
          relative w-full mx-auto rounded-3xl p-10 md:py-16 md:px-[120px] text-center
           bg-dark-grey-2
          border border-beige/50
        "
          >
            {/* Subtle inner corner glow */}
            <div
              className="pointer-events-none absolute inset-0 rounded-3xl opacity-100"
              style={{
                background: `
                radial-gradient(circle at top left, rgba(225, 202, 161, 0.20) 0%, transparent 35%),
                radial-gradient(circle at bottom right, rgba(225, 202, 161, 0.20) 0%, transparent 35%)
              `,
              }}
            />

            <h2 className="text-5xl md:text-7xl font-semibold mb-6 text-white">
              Ready to Elevate Your <br />
              <span className="text-golden">Production Game?</span>
            </h2>

            <p className="text-white text-2xl mb-10 mx-auto font-light">
              Join thousands of production teams streamlining their workflow
              with Beige. Start your journey to production excellence today.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                className="golden-button large-button w-60" // Added w-60
                // onClick={() => (window.location.href = "/login")}
              >
                Access Portal
              </Button>

              <Button
                className="glass-button large-button w-60" // Added w-60
                onClick={() => (window.location.href = "https://beige.app")}
              >
                Visit Beige.app
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* --- Glowing Beige Section --- */}
      <section
        id="glowing-text"
        className="relative py-14 px-4 md:px-0 overflow-hidden bg-dark-grey"
      >
        <div className="container mx-auto relative z-10 text-center">
          <GlowingText className="w-full" />
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="bg-dark-grey text-white/40 pb-12 text-sm">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Logo Column */}
            <div className="col-span-1 md:col-span-1">
              <div className="mb-6">
                <img
                  src="/images/logos/beige_logo_vb.png"
                  alt="BEIGE"
                  className="h-8 w-auto object-contain"
                />
              </div>
              <p className="leading-relaxed text-sm text-beige-2">
                The all-in-one platform for livestream operations management.
              </p>
            </div>

            {/* Links Columns */}
            <div>
              <h4 className="text-white text-base font-medium mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="#"
                    className="text-beige-2 hover:text-golden transition-colors"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-beige-2 hover:text-golden transition-colors"
                  >
                    Pricing
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-beige-2 hover:text-golden transition-colors"
                  >
                    API
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-beige-2 hover:text-golden transition-colors"
                  >
                    Integrations
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-base font-medium mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="#"
                    className="text-beige-2 hover:text-golden transition-colors"
                  >
                    About
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-beige-2 hover:text-golden transition-colors"
                  >
                    Blog
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-beige-2 hover:text-golden transition-colors"
                  >
                    Careers
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-beige-2 hover:text-golden transition-colors"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>
            <div className="flex flex-col items-center md:items-start">
              <h4 className="text-white text-base font-medium mb-4 text-center md:text-left">
                Get in Touch
              </h4>
              <Button className="golden-button small-button mb-3 w-60">
                Chat with Us
              </Button>
              <Button
                className="glass-button small-button w-60"
                onClick={() => (window.location.href = "https://beige.app")}
              >
                Visit Beige.app
              </Button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-[#E7E5E4]">
            <p className="text-beige-2 text-sm">
              © 2026 Beige. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm mt-4 md:mt-0">
              <a
                href="#"
                className="text-beige-2 hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className="text-beige-2 hover:text-white transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="#"
                className="text-beige-2 hover:text-white transition-colors"
              >
                Security
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};


const StepCard = ({ number, title, description }) => (
  <div
    className="
      group relative z-10 p-[1px] transition-all duration-500
      md:border-r border-beige border-b lg:border-b-0
    "
  >
    {/* Gradient border (bottom-left → top-right) */}
    <div
      className="
        absolute inset-0
        bg-gradient-to-tr from-golden/40 via-golden/10 to-transparent
        opacity-0 group-hover:opacity-100
        transition-opacity duration-500
        pointer-events-none
      "
    />

    {/* Inner card */}
    <div
      className="
        relative px-6 py-10 bg-[#080808]
        overflow-hidden transition-all duration-500 h-full
      "
    >

      {/* Inner card glow (top-right → bottom-left ~20%) */}
      <div
        className="
          absolute inset-0
          bg-gradient-to-bl from-golden/20 via-golden/10 to-transparent
          opacity-0 group-hover:opacity-50 transition-opacity duration-500
          pointer-events-none
        "
      />

      {/* Number Bubble */}
      <div
        className="
          h-10 w-10 p-2.5 rounded-full
          bg-beige border border-white/20
          flex items-center justify-center
          text-lg font-medium text-black mb-6
          relative z-10
        "
      >
        {number}
      </div>

      {/* Title */}
      <h3 className="text-3xl font-semibold mb-14 text-white whitespace-pre-line relative z-10">
        {title}
      </h3>

      {/* Description */}
      <p className="text-base text-white leading-relaxed font-light relative z-10">
        {description}
      </p>
    </div>
  </div>
);

export default LandingPage;

const BackgroundVideo = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Replay when section comes into view
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.currentTime = 0;
          video.play().catch(() => { });
        } else {
          video.pause();
        }
      },
      { threshold: 0.4 } // trigger when 40% visible
    );

    const section = video.closest("section");
    if (section) observer.observe(section);

    // Replay every few seconds (even if loop is on)
    const interval = setInterval(() => {
      if (!video.paused) {
        video.currentTime = 0;
        video.play().catch(() => { });
      }
    }, 9000); // adjust time (ms) as needed

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  return (
    <video
      ref={videoRef}
      className="absolute inset-0 w-full h-full object-cover z-0"
      src="https://beigexmemehouse.s3.eu-north-1.amazonaws.com/beige/beige.landingpage.mp4"
      muted
      playsInline
      preload="auto"
    />
  );
};
